<zag-floating-panel
  [panelTitle]="'verification.sidebar.title' | translate"
  [collapsed]="collapsed()"
  (collapsedChange)="onCollapsedChange($event)"
  [position]="position()"
  (positionChange)="onPositionChange($event)"
  [expandAriaLabel]="'verification.sidebar.expand' | translate"
  [collapseAriaLabel]="'verification.sidebar.collapse' | translate"
  [resetPositionAriaLabel]="'verification.sidebar.resetPosition' | translate"
>
  <span zagFloatingPanelPill class="sc-test-feedback__pill-label">
    @if (loadFailed()) {
      <span zagIcon="help" aria-hidden="true"></span>
    } @else {
      {{ progressLabel() }}
    }
  </span>

  @if (isLoading()) {
    <p class="sc-test-feedback__hint">{{ 'verification.sidebar.loading' | translate }}</p>
  } @else if (loadFailed()) {
    <p class="sc-test-feedback__hint">{{ 'verification.sidebar.noScript' | translate }}</p>
  } @else if (script(); as testScript) {
    @switch (feedback.submitState().kind) {
      @case ('done') {
        <div class="sc-test-feedback__done">
          <p class="sc-test-feedback__done-title">
            <span zagIcon="check-circle" aria-hidden="true"></span>
            {{ 'verification.sidebar.doneTitle' | translate }}
          </p>
          @if (feedback.lastCommentUrl(); as commentUrl) {
            <a class="sc-test-feedback__link" [href]="commentUrl" target="_blank" rel="noopener">
              {{ 'verification.sidebar.viewComment' | translate }}
            </a>
          }
          <button type="button" class="sc-test-feedback__button" (click)="feedback.startNewReview()">
            {{ 'verification.sidebar.newReview' | translate }}
          </button>
        </div>
      }
      @default {
        @if (feedback.buildChanged()) {
          <p class="sc-test-feedback__notice">{{ 'verification.sidebar.buildChanged' | translate }}</p>
        }

        @if (testScript.preconditions.length > 0) {
          <section class="sc-test-feedback__section">
            <h3 class="sc-test-feedback__section-title">{{ 'verification.sidebar.preconditions' | translate }}</h3>
            <ul class="sc-test-feedback__list">
              @for (precondition of testScript.preconditions; track precondition) {
                <li>{{ precondition }}</li>
              }
            </ul>
          </section>
        }

        <section class="sc-test-feedback__section">
          <h3 class="sc-test-feedback__section-title">{{ 'verification.sidebar.testCases' | translate }}</h3>
          <ul class="sc-test-feedback__cases">
            @for (testCase of testScript.testCases; track testCase.id) {
              <li
                class="sc-test-feedback__case"
                [class.sc-test-feedback__case--passed]="resultFor(testCase.id)?.status === 'passed'"
                [class.sc-test-feedback__case--failed]="resultFor(testCase.id)?.status === 'failed'"
              >
                <div class="sc-test-feedback__case-row">
                  <button
                    type="button"
                    class="sc-test-feedback__case-toggle"
                    (click)="toggleCase(testCase.id)"
                    [attr.aria-expanded]="expandedCases().has(testCase.id)"
                  >
                    <span [zagIcon]="expandedCases().has(testCase.id) ? 'expand-more' : 'chevron-right'" aria-hidden="true"></span>
                    <span class="sc-test-feedback__case-id">{{ testCase.id }}</span>
                    <span class="sc-test-feedback__case-title">{{ testCase.title }}</span>
                  </button>
                  <span class="sc-test-feedback__case-actions">
                    <button
                      type="button"
                      class="sc-test-feedback__result-button sc-test-feedback__result-button--pass"
                      [class.sc-test-feedback__result-button--active]="resultFor(testCase.id)?.status === 'passed'"
                      [attr.aria-label]="'verification.sidebar.markPassed' | translate: { id: testCase.id }"
                      [attr.aria-pressed]="resultFor(testCase.id)?.status === 'passed'"
                      (click)="markPassed(testCase.id)"
                    >
                      <span zagIcon="check" aria-hidden="true"></span>
                    </button>
                    <button
                      type="button"
                      class="sc-test-feedback__result-button sc-test-feedback__result-button--fail"
                      [class.sc-test-feedback__result-button--active]="resultFor(testCase.id)?.status === 'failed'"
                      [attr.aria-label]="'verification.sidebar.markFailed' | translate: { id: testCase.id }"
                      [attr.aria-pressed]="resultFor(testCase.id)?.status === 'failed'"
                      (click)="openFailEditor(testCase.id)"
                    >
                      <span zagIcon="close" aria-hidden="true"></span>
                    </button>
                  </span>
                </div>

                @if (expandedCases().has(testCase.id)) {
                  <dl class="sc-test-feedback__case-details">
                    <dt>{{ 'verification.sidebar.given' | translate }}</dt>
                    <dd>{{ testCase.given }}</dd>
                    <dt>{{ 'verification.sidebar.when' | translate }}</dt>
                    <dd>{{ testCase.when }}</dd>
                    <dt>{{ 'verification.sidebar.then' | translate }}</dt>
                    <dd>{{ testCase.then }}</dd>
                    @if (testCase.coversAcs.length > 0) {
                      <dt>{{ 'verification.sidebar.covers' | translate }}</dt>
                      <dd>AC {{ testCase.coversAcs.join(', ') }}</dd>
                    }
                  </dl>
                }

                @if (resultFor(testCase.id)?.status === 'failed' && failEditor()?.caseId !== testCase.id) {
                  <p class="sc-test-feedback__fail-comment">{{ resultFor(testCase.id)?.comment }}</p>
                }

                @if (failEditor(); as editor) {
                  @if (editor.caseId === testCase.id) {
                    <div class="sc-test-feedback__fail-editor">
                      <label class="sc-test-feedback__label" [for]="'sc-fail-comment-' + testCase.id">
                        {{ 'verification.sidebar.commentLabel' | translate }}
                      </label>
                      <textarea
                        class="sc-test-feedback__textarea"
                        [id]="'sc-fail-comment-' + testCase.id"
                        rows="3"
                        maxlength="2000"
                        [value]="editor.comment"
                        (input)="updateFailComment($any($event.target).value)"
                        [attr.placeholder]="'verification.sidebar.commentPlaceholder' | translate"
                      ></textarea>
                      @if (failEditorMissingComment()) {
                        <p class="sc-test-feedback__error" role="alert">
                          {{ 'verification.sidebar.commentRequired' | translate }}
                        </p>
                      }
                      <div class="sc-test-feedback__editor-actions">
                        <button type="button" class="sc-test-feedback__button" (click)="cancelFailEditor()">
                          {{ 'common.cancel' | translate }}
                        </button>
                        <button type="button" class="sc-test-feedback__button sc-test-feedback__button--danger" (click)="saveFailResult()">
                          {{ 'verification.sidebar.saveFail' | translate }}
                        </button>
                      </div>
                    </div>
                  }
                }
              </li>
            }
          </ul>
        </section>

        @if (testScript.regressionChecks.length > 0) {
          <section class="sc-test-feedback__section">
            <h3 class="sc-test-feedback__section-title">{{ 'verification.sidebar.regressionChecks' | translate }}</h3>
            <ul class="sc-test-feedback__list">
              @for (check of testScript.regressionChecks; track check) {
                <li>{{ check }}</li>
              }
            </ul>
          </section>
        }

        <footer class="sc-test-feedback__footer">
          <label class="sc-test-feedback__label" for="sc-test-feedback-tester">
            {{ 'verification.sidebar.testerLabel' | translate }}
          </label>
          <input
            class="sc-test-feedback__input"
            id="sc-test-feedback-tester"
            type="text"
            maxlength="100"
            [value]="testerName()"
            (input)="updateTesterName($any($event.target).value)"
          />

          @if (submitErrorKey(); as errorKey) {
            <p class="sc-test-feedback__error" role="alert">{{ errorKey | translate }}</p>
            <button type="button" class="sc-test-feedback__button" (click)="retrySubmit()">
              {{ 'verification.sidebar.retry' | translate }}
            </button>
          }

          <button
            type="button"
            class="sc-test-feedback__button sc-test-feedback__button--primary"
            [disabled]="!canFinish() || !testerName().trim()"
            (click)="finishReview()"
          >
            @if (feedback.submitState().kind === 'submitting') {
              {{ 'verification.sidebar.submitting' | translate }}
            } @else {
              {{ 'verification.sidebar.finish' | translate }}
            }
          </button>
        </footer>
      }
    }
  }
</zag-floating-panel>
